import { SpeechToTextProps } from '@custom/Utils/constants/interfaces';
import { supportedLanguages } from './supportedLanguages';
import { Icon } from '@ohif/ui';
import React, { useState, useEffect, useRef } from 'react'
import { useVoice } from './Hooks/useVoice';
import voiceService from '../services/voiceService';
import { useTheme } from '../../ui/src/contextProviders';

export default function SpeechToText({ contentDiv }: SpeechToTextProps) {

  const [focusedElement, setFocusedElement] = useState(null); // Store the focused element
  const focusedElementRef = useRef(null); // Use a ref to track focused element more reliably
  const [isAudioEnabled, setIsAudioEnabled] = useState(false);
  const [textValue, setTextValue] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false); // Track connection status
  const [subtitles, setSubtitles] = useState<string[]>([]);
  const lastProcessedText = useRef(''); // Track the last processed text to prevent duplicates
  const processingRef = useRef(false); // Flag to prevent concurrent processing
  const textQueue = useRef<string[]>([]); // Queue to store incoming text chunks
  const latestSpeechText = useRef(''); // Store the latest full text from speech API
  const processingTimeoutRef = useRef(null); // Reference to the timeout for processing
  const speechContentMap = useRef(new Map());
  const activeQlEditorRef = useRef(null); // Store the active ql-editor
  const fullCombinedTextRef = useRef('');
  const originalContentMap = useRef(new Map());
  const [selectedInputLanguage, setSelectedInputLanguage] = useState('en'); // Default language
  const [selectedOutputLanguage, setSelectedOutputLanguage] = useState('en'); // Default language
  const isTranslatedRef = useRef(false); // Track if the current text is translated
  const { isDarkMode } = useTheme();

  // Add this function to handle language change
  const handleInputLanguageChange = (event) => {
    setSelectedInputLanguage(event.target.value);
  };

  const handleOutputLanguageChange = (event) => {
    setSelectedOutputLanguage(event.target.value);
  };

  // Find and return the first ql-editor
  const findFirstQlEditor = () => {
    const targetDiv = document.getElementById(contentDiv);
    if (targetDiv) {
      return targetDiv.querySelector('.ql-editor') as HTMLDivElement;
    }
    return null;
  };

  // Handle text updates from the speech API
  const handleTextChange = (text, isTranslated = false) => {
    console.log("Speech API update:", text, "isTranslated:", isTranslated);
    isTranslatedRef.current = isTranslated;

    // Skip empty text
    if (!text || text.trim() === '') return;

    let newFullText = text;

    // If we receive a translated result, we should use it as provided by voiceService
    // The voiceService now handles appending new translations to existing ones
    if (isTranslated) {
      // For translated text, we use what we received from voiceService
      // which now contains the combined translations
      console.log("Using combined translated text:", text);
      newFullText = text;
    } else {
      // For non-translated text (same language), apply continuation logic
      const previousFullText = fullCombinedTextRef.current;

      // Check if the new text is a subset/continuation of previous text
      if (previousFullText && text.length < previousFullText.length) {
        // If new text is shorter, check if it appears to be continuing from a point in the previous text
        const possibleStartPositions = [];

        // Find all possible positions where this text could be starting from the previous text
        let startPos = previousFullText.indexOf(text.substring(0, Math.min(30, text.length)));
        while (startPos !== -1) {
          possibleStartPositions.push(startPos);
          startPos = previousFullText.indexOf(text.substring(0, Math.min(30, text.length)), startPos + 1);
        }

        if (possibleStartPositions.length > 0) {
          // Use the position closest to the end of previous text
          const bestStartPos = Math.max(...possibleStartPositions);
          console.log("Text appears to be continuing from position", bestStartPos);

          // Check if this text adds any new content beyond what we already have
          if (previousFullText.length > bestStartPos + text.length) {
            // The new text doesn't add anything new, so just keep the existing text
            console.log("New text doesn't add new content, keeping existing text");
            newFullText = previousFullText;
          } else {
            // This text continues and adds new content
            const existingPart = previousFullText.substring(0, bestStartPos);
            console.log("Appending continuing text");
            newFullText = existingPart + text;
          }
        } else {
          // This might be a new session or the text is very different
          console.log("Text appears to be from a new session or unrelated");
          newFullText = text;
        }
      } else if (previousFullText && previousFullText.includes(text.substring(0, Math.min(text.length, 30)))) {
        // Current text seems to be an extension of previous text
        console.log("Text appears to be an extension of previous");

        // Check which parts of the new text are actually new
        const overlap = findLargestOverlap(previousFullText, text);

        if (overlap.length > 30) {  // Significant overlap found
          console.log("Found significant overlap of", overlap.length, "characters");
          const newContent = text.substring(overlap.length);

          if (newContent.trim()) {
            console.log("Adding new content:", newContent);
            newFullText = previousFullText + newContent;
          } else {
            newFullText = previousFullText;
          }
        } else {
          // Not enough overlap, treat as separate
          console.log("Insufficient overlap, treating as new content");
          newFullText = text;
        }
      }
    }

    console.log("Full text length:", newFullText.length);

    // Store the latest full text
    latestSpeechText.current = text;
    fullCombinedTextRef.current = newFullText;

    // Check focused element both in state and ref
    console.log("Focused element when updating:",
      focusedElement,
      Boolean(focusedElement),
      "Ref:",
      focusedElementRef.current,
      Boolean(focusedElementRef.current),
      "Active ql-editor:",
      activeQlEditorRef.current,
      Boolean(activeQlEditorRef.current)
    );

    // Try different approaches to find a target element
    const targetElement = focusedElementRef.current || focusedElement || activeQlEditorRef.current || findFirstQlEditor();

    if (targetElement) {
      console.log("Using target element:", targetElement.tagName);
      replaceContent(newFullText, targetElement);
    } else {
      console.log("No target element found to update");
    }
  };

  // Helper function to find the largest overlapping section between two strings
  const findLargestOverlap = (str1, str2) => {
    // Start with the largest possible overlap
    for (let overlapSize = Math.min(str1.length, str2.length); overlapSize > 0; overlapSize--) {
      const str1End = str1.substring(str1.length - overlapSize);
      const str2Start = str2.substring(0, overlapSize);

      if (str1End === str2Start) {
        return str1End;
      }
    }
    return "";
  };

  // Replace the entire speech content
  const replaceContent = (text, targetElement = null) => {
    const element = targetElement || focusedElementRef.current || focusedElement;

    if (!element) {
      console.log("Cannot replace content - no element target");
      return;
    }

    console.log("Replacing content in", element.tagName, "with new full text");

    if (element.tagName === 'TEXTAREA' || element.tagName === 'INPUT') {
      const inputEl = element as HTMLInputElement | HTMLTextAreaElement;

      // Get original content that was in the field before speech started
      const originalContent = originalContentMap.current.get(element) || '';
      console.log("Original content prefix:", originalContent.substring(0, 20));

      // Replace with original content + new speech text
      inputEl.value = originalContent + text;

      // Store that this element now has speech content
      speechContentMap.current.set(element, true);

      // Position cursor at the end
      inputEl.selectionStart = inputEl.selectionEnd = inputEl.value.length;
      const event = new Event('input', { bubbles: true });
      inputEl.dispatchEvent(event);

      console.log("Updated input value, new length:", inputEl.value.length);
    } else if (element.isContentEditable) {
      // For contentEditable elements like ql-editor
      console.log("Content editable - current length:", element.textContent?.length);

      // Get original content that was in the editor before speech started
      const originalContent = originalContentMap.current.get(element) || '';

      // Replace content with original + new text
      if (!speechContentMap.current.has(element)) {
        // First time adding speech content
        // Keep existing content as is and just append new text
        const textNode = document.createTextNode(text);
        element.appendChild(textNode);
      } else {
        // For subsequent updates, preserve original content
        element.innerHTML = originalContent;
        const textNode = document.createTextNode(text);
        element.appendChild(textNode);
      }

      // Store that this element now has speech content
      speechContentMap.current.set(element, true);

      // Move cursor to end
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        const range = document.createRange();
        range.selectNodeContents(element);
        range.collapse(false); // collapse to end
        selection.addRange(range);
        element.focus();
      }

      console.log("Content editable - updated length:", element.textContent?.length);
    }
  };

  // Force socket restart when changing focused element
  useEffect(() => {
    if (isRecording && focusedElement) {
      console.log("Focused element changed during recording:", focusedElement.tagName);

      // Update the ref to match the state
      focusedElementRef.current = focusedElement;

      // Close previous connection and start a new one
      (async () => {
        stopStreaming();
        // Clear any pending text
        textQueue.current = [];
        latestSpeechText.current = '';
        lastProcessedText.current = '';
        clearTimeout(processingTimeoutRef.current);

        // Short delay to ensure proper socket closure
        await new Promise(resolve => setTimeout(resolve, 100));
        setIsConnecting(true);
        const success = await connectToWhisper();
        setIsConnecting(false);
        if (success !== false) {
          await startStreaming();
        }
      })();
    }
  }, [focusedElement]);

  // Monitor for active ql-editors
  useEffect(() => {
    // Find and keep track of ql-editors
    const findAndUpdateQlEditors = () => {
      const firstQlEditor = findFirstQlEditor();
      if (firstQlEditor) {
        activeQlEditorRef.current = firstQlEditor;
      }
    };

    // Initial check
    findAndUpdateQlEditors();

    // Set up periodic checks
    const intervalId = setInterval(findAndUpdateQlEditors, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [contentDiv]);

  const handleStart = async () => {
    document.addEventListener('focusin', handleFocus);

    // Reset all text tracking state
    textQueue.current = [];
    latestSpeechText.current = '';
    lastProcessedText.current = '';
    clearTimeout(processingTimeoutRef.current);
    fullCombinedTextRef.current = '';
    isTranslatedRef.current = false;

    // Find and focus the first ql-editor element within the content div
    const firstQlEditor = findFirstQlEditor();
    if (firstQlEditor) {
      console.log("Focusing first ql-editor element");

      // Store the original content before we start
      originalContentMap.current.set(firstQlEditor, firstQlEditor.innerHTML);
      console.log("Stored original ql-editor content, length:", firstQlEditor.innerHTML.length);

      firstQlEditor.focus();

      // Create selection at the end of content
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(firstQlEditor);
      range.collapse(false); // collapse to end
      selection.removeAllRanges();
      selection.addRange(range);

      // Update both state and ref for focused element
      setFocusedElement(firstQlEditor);
      focusedElementRef.current = firstQlEditor;
      activeQlEditorRef.current = firstQlEditor;

      console.log("Set focused element to:", firstQlEditor.tagName);
    } else {
      console.log("No ql-editor found to focus");
    }

    // Configure the voice service with current settings
    voiceService.configure({
      language: selectedOutputLanguage,
      inputLanguage: selectedInputLanguage,
      setTextAreaValue: handleTextChange,
      setIsRecordingInProcess: setIsRecording,
      isTranscribe: true,
      setRunOnValue: setSubtitles,
    });

    setIsConnecting(true);
    const success = await voiceService.connectToWhisper();
    setIsConnecting(false);

    if (success !== false) {
      await voiceService.startStreaming();
    } else {
      alert("Servers are busy. Please try again.");
    }
  };

  const handleStop = () => {
    document.removeEventListener('focusin', handleFocus);

    voiceService.cancelRequested = true;

    const socketState = voiceService.getSocketState();

    if (socketState === WebSocket.OPEN) {
      voiceService.stopStreaming();
    } else {
      voiceService.deleteStreaming();
    }

    setIsRecording(false);
    setIsConnecting(false);
    setTextValue('');
    lastProcessedText.current = '';
    textQueue.current = [];
    latestSpeechText.current = '';
    fullCombinedTextRef.current = '';
    processingRef.current = false;
    clearTimeout(processingTimeoutRef.current);

    // Clear the speech content map when stopping
    speechContentMap.current.clear();
  };

  const handleFocus = async (event: FocusEvent) => {
    // Skip focus handling if we're still connecting
    if (isConnecting) return;

    const target = event.target as HTMLElement;

    const isValidTarget =
      target?.classList?.contains('ql-editor') ||
      target?.id === 'impression' ||
      target?.id === 'comment' ||
      (target?.tagName === 'TEXTAREA' || target?.tagName === 'INPUT');

    // If we have a valid target
    if (isValidTarget) {
      console.log("Focused new element:", target.tagName);

      // Store the original content of the focused element
      if (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT') {
        const inputEl = target as HTMLInputElement | HTMLTextAreaElement;
        originalContentMap.current.set(target, inputEl.value);
        console.log("Stored original input content:", inputEl.value.substring(0, 20));
      } else if (target.isContentEditable) {
        originalContentMap.current.set(target, target.innerHTML);
        console.log("Stored original contentEditable content, length:", target.innerHTML.length);
      }

      // Reset state when changing focus
      lastProcessedText.current = '';
      textQueue.current = [];
      latestSpeechText.current = '';
      processingRef.current = false;
      clearTimeout(processingTimeoutRef.current);

      // Update both state and ref
      setFocusedElement(target);
      focusedElementRef.current = target;

      if (target.classList?.contains('ql-editor')) {
        activeQlEditorRef.current = target;
      }

      // When switching to a new element, we start fresh
      if (isRecording && latestSpeechText.current) {
        // A slight delay to ensure focus is complete
        setTimeout(() => {
          replaceContent(latestSpeechText.current, target);
        }, 100);
      }
    } else {
      // Keep the reference but update state
      setFocusedElement(null);
    }
  };

  // We still use the hook to get the functions for other parts of the component
  const [
    stopStreaming,
    startStreaming,
    deleteStreaming,
    connectToWhisper,
    pauseStreaming,
    getSocketState
  ] = useVoice({
    language: selectedOutputLanguage,
    inputLanguage: selectedInputLanguage,
    setTextAreaValue: handleTextChange,
    setIsRecordingInProcess: setIsRecording,
    isTrascribe: true,
    setRunOnValue: setSubtitles,
  });

  // Add a blocking overlay when connecting
  useEffect(() => {
    if (isConnecting) {
      // Add event listener to prevent focus events
      const blockFocus = (e) => {
        if (e.target?.tagName === 'TEXTAREA' ||
            e.target?.tagName === 'INPUT' ||
            e.target?.classList?.contains('ql-editor')) {
          originalContentMap.current.set(e.target, e.target.value);
          e.preventDefault();
          e.stopPropagation();
        }
      };

      document.addEventListener('mousedown', blockFocus, true);
      document.addEventListener('focusin', blockFocus, true);

      return () => {
        document.removeEventListener('mousedown', blockFocus, true);
        document.removeEventListener('focusin', blockFocus, true);
      };
    }
  }, [isConnecting]);

  useEffect(() => {
    // Skip if no text to process or no focused element
    if (!focusedElement || textValue === '') {
      processingRef.current = false;
      return;
    }

    const targetDiv = document.getElementById(contentDiv);
    if (!targetDiv?.contains(focusedElement)) {
      processingRef.current = false;
      return;
    }

    const insertText = () => {
      console.log("Inserting text:", textValue);

      if (focusedElement.tagName === 'TEXTAREA' || focusedElement.tagName === 'INPUT') {
        const inputEl = focusedElement as HTMLInputElement | HTMLTextAreaElement;
        // Get current content and check if text already exists
        const currentContent = inputEl.value;

        // Check if the text (or part of it) already exists in the content
        if (currentContent.includes(textValue)) {
          console.log("Text already exists, skipping insertion");
          return;
        }

        const newContent = currentContent + textValue;

        // Replace entire content
        inputEl.value = newContent;

        // Position cursor at the end
        inputEl.selectionStart = inputEl.selectionEnd = newContent.length;
        const event = new Event('input', { bubbles: true });
        inputEl.dispatchEvent(event);
      } else if (focusedElement.isContentEditable) {
        // For contentEditable elements like ql-editor
        // Get current content
        const currentContent = focusedElement.innerHTML;
        const currentText = focusedElement.textContent || '';

        // Check if the text (or part of it) already exists in the current text content
        if (currentText.includes(textValue)) {
          console.log("Text already exists in contentEditable, skipping insertion");
          return;
        }

        // Create a text node for the new content
        const textNode = document.createTextNode(textValue);

        // Append to existing content and set it all at once
        focusedElement.appendChild(textNode);

        // Move cursor to end - improved implementation
        const selection = window.getSelection();
        if (selection) {
          // Clear any existing selection first
          selection.removeAllRanges();

          // Create a new range at the end of the element
          const range = document.createRange();
          range.selectNodeContents(focusedElement);
          range.collapse(false); // collapse to end

          // Apply the selection and ensure focus
          selection.addRange(range);
          focusedElement.focus();
        }
      }

      // Update last processed text to include what we just added
      lastProcessedText.current = latestSpeechText.current;
    };

    // Insert the text and reset state
    insertText();
    setTextValue('');

    // Allow processing the next item in queue
    setTimeout(() => {
      processingRef.current = false;
    }, 50);
  }, [textValue, focusedElement, contentDiv]);

  // Reset the full combined text when language selection changes
  useEffect(() => {
    fullCombinedTextRef.current = '';
    latestSpeechText.current = '';
    lastProcessedText.current = '';
  }, [selectedInputLanguage, selectedOutputLanguage]);

  return (
  <>
      <div className='flex items-center gap-2'>
        <div>
          <div className="formCol flex items-center">
            {!isRecording ? (
              <Icon
                onClick={isConnecting ? null : handleStart}
                name="Microphone"
                className={`text-black ${isConnecting ? 'opacity-50' : 'cursor-pointer'}`}
                style={{ width: '50px', height: '50px' }}
              />
            ) : (
              <button onClick={handleStop}>
                <Icon name="Stop" className="text-black" style={{ width: '50px', height: '44px', padding: "4px 0" }} />
              </button>
            )}
            <p
              style={{
                color: isDarkMode ? 'white' : 'black',
                fontFamily: 'Poppins, sans-serif',
                fontSize: '14px',
                fontWeight: 400,
                textAlign: 'center'
              }}
            >
              {isConnecting ? ' Connecting...' : isRecording ? ' Listening ...' : ' Speech to Text'}
            </p>
          </div>
        </div>
      </div>
    <div className="form form--innersec">
        <div className="form__section" style={{ backgroundColor: isDarkMode ? "#090c29" : '#d2e2e0', padding: "0.5rem" }}>
        <div className="formRow formRow__2 mb-0">
          <div className="formCol">
            <div className="form__Field">
              <label className='form__label' htmlFor='inputLanguage'>
                Input Language
              </label>
                <select className='form__select form__select--sm' id="inputLanguage" value={selectedInputLanguage} onChange={handleInputLanguageChange}>
                  {supportedLanguages.map(lang => (
                    <option style={{ backgroundColor: isDarkMode ? '#090c29' : 'white', color: isDarkMode ? 'white' : 'black' }} key={lang.value} value={lang.value}>{lang.label}</option>
                  ))}
              </select>
            </div>
          </div>
          <div className="formCol">
            <div className="form__Field">
              <label className='form__label' htmlFor='outputLanguage'>
                Output Language
              </label>
                <select className='form__select form__select--sm' id="outputLanguage" value={selectedOutputLanguage} onChange={handleOutputLanguageChange}>
                  {supportedLanguages.map(lang => (
                    <option style={{ backgroundColor: isDarkMode ? '#090c29' : 'white', color: isDarkMode ? 'white' : 'black' }} key={lang.value} value={lang.value}>{lang.label}</option>
                  ))}
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  </>
  )
}
